import { type ReactNode, useRef, useState } from 'react';
import { cn } from '../cn';
import { Icon } from '../icons';
import { Button } from '../primitives';
import s from './records.module.css';

/** The rules are stated before anything is selected (DOC-02). */
export function DropZone({
  accept,
  rules,
  onFiles,
  disabled,
}: {
  accept: string;
  rules: ReactNode;
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drop target; the button inside is the keyboard path
    <div
      className={cn(s.drop, over && s.over)}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (!disabled) onFiles([...e.dataTransfer.files]);
      }}
    >
      <span className={s.ic}>
        <Icon.Upload />
      </span>
      <span className={s.h}>Drop files here or choose from your device</span>
      <span className={s.rules}>{rules}</span>
      <Button
        variant="sec"
        style={{ marginTop: '.4rem' }}
        onClick={() => input.current?.click()}
        disabled={disabled}
      >
        Choose files
      </Button>
      <span className={s.or}>On a phone you can take a photo of a report directly.</span>
      <input
        ref={input}
        type="file"
        multiple
        accept={accept}
        hidden
        aria-label="Choose files"
        onChange={(e) => {
          onFiles([...(e.target.files ?? [])]);
          e.target.value = '';
        }}
      />
    </div>
  );
}

export type UploadState =
  | { kind: 'uploading'; loaded: number; total: number }
  | { kind: 'needs'; sizeLabel: string }
  | { kind: 'ready'; sizeLabel: string }
  | { kind: 'error'; message: string };

/** One file in the upload list: progress, required description, named rejection (DOC-01, DOC-03). */
export function UploadItem({
  ext,
  name,
  state,
  description,
  onDescribe,
  onDelete,
  locked,
}: {
  ext: string;
  name: string;
  state: UploadState;
  description: string;
  onDescribe: (v: string) => void;
  onDelete: () => void;
  locked?: boolean;
}) {
  const pct = state.kind === 'uploading' ? Math.round((state.loaded / Math.max(1, state.total)) * 100) : 0;
  const mb = (n: number) => `${(n / 1024 / 1024).toFixed(1)} MB`;
  return (
    <div className={cn(s.fi, state.kind === 'needs' && s.needs, state.kind === 'error' && s.err)}>
      <span className={s.ic}>{ext}</span>
      <div className={s.n1}>{name}</div>
      <div className={s.acts}>
        <button type="button" className={s.del} onClick={onDelete} disabled={locked}>
          {state.kind === 'uploading' ? 'Cancel' : state.kind === 'error' ? 'Dismiss' : 'Delete'}
        </button>
      </div>
      <div className={s.n2}>
        {state.kind === 'uploading' && `Uploading · ${mb(state.loaded)} of ${mb(state.total)}`}
        {state.kind === 'ready' && (
          <>
            <span className={s['done-mark']}>
              <i />
              Uploaded
            </span>{' '}
            · {state.sizeLabel}
          </>
        )}
        {state.kind === 'needs' && `Uploaded · ${state.sizeLabel} · needs a description before you continue`}
        {state.kind === 'error' && state.message}
      </div>
      {state.kind === 'uploading' && (
        <div className={s.prog} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <span className={s.tr}>
            <i style={{ width: `${pct}%` }} />
          </span>
          <span>{pct}%</span>
        </div>
      )}
      {(state.kind === 'ready' || state.kind === 'needs') && (
        <div className={s.desc}>
          <span className={s.lbl}>{state.kind === 'needs' ? 'What is this file?' : 'Description'}</span>
          <input
            className="input"
            aria-label={`Description for ${name}`}
            placeholder="For example: surgical pathology report, May 2026"
            defaultValue={description}
            readOnly={locked}
            style={state.kind === 'needs' ? { borderColor: 'var(--alert)' } : undefined}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v && v !== description) onDescribe(v);
            }}
          />
        </div>
      )}
    </div>
  );
}

export function FreezeNote({ children }: { children: ReactNode }) {
  return (
    <div className={s.freeze}>
      <Icon.Lock width={14} height={14} />
      <span>{children}</span>
    </div>
  );
}

export function UploadList({ children }: { children: ReactNode }) {
  return <div className={s.flist}>{children}</div>;
}
